import { useEffect, useMemo, useRef, type FC } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PratusSelect, {
  type OptionType,
} from 'components/DesignSystems/New/PratusSelect';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import { ICS205A_COLUMN_LABELS, ICS205A_TABLE_MIN_WIDTH_PX } from './constants';
import { contactKey } from './draftUtils';
import {
  ICS205A_ICON_BTN,
  ICS205A_INPUT,
  ICS205A_TRASH_BTN,
} from './ics205aStyles';
import type { Ics205aContactPatch, Ics205aDraftContact } from './types';

const CELL =
  'tw:border-b tw:border-neutral-800 tw:px-2 tw:py-2 tw:align-top tw:text-white';
const HEADER_CELL =
  'tw:border-b tw:border-neutral-700 tw:bg-neutral-900 tw:px-2 tw:py-2 tw:text-left tw:text-[10px] tw:font-semibold tw:tracking-wide tw:text-neutral-300';

export type Ics205aContactRowsTableProps = {
  contacts: Ics205aDraftContact[];
  canEdit: boolean;
  focusContactId: string | null;
  onFocusConsumed: () => void;
  onPatchContact: (
    contact: Ics205aDraftContact,
    patch: Ics205aContactPatch
  ) => void;
  onDeleteContact: (contact: Ics205aDraftContact) => void;
};

export const Ics205aContactRowsTable: FC<Ics205aContactRowsTableProps> = ({
  contacts,
  canEdit,
  focusContactId,
  onFocusConsumed,
  onPatchContact,
  onDeleteContact,
}) => {
  const nameInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const { data: positions } = useGetPositionsForIncident();

  const positionOptions = useMemo<OptionType[]>(() => {
    const seen = new Set<string>();
    const opts: OptionType[] = [];
    for (const pos of positions ?? []) {
      // A 205A contact is a person holding a seat, so resources are not offered here.
      if (pos.isResourcePosition) continue;
      if (!seen.has(pos.name)) {
        seen.add(pos.name);
        opts.push({ value: pos.name, label: pos.name });
      }
    }
    opts.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return opts;
  }, [positions]);

  useEffect(() => {
    if (!focusContactId) return;
    const input = nameInputRefs.current[focusContactId];
    if (input) {
      input.focus();
      input.select();
    }
    onFocusConsumed();
  }, [focusContactId, onFocusConsumed]);

  return (
    <div className="tw:w-0 tw:min-w-full tw:overflow-x-auto">
      <table
        className="tw:w-full tw:border-collapse tw:text-xs"
        style={{ minWidth: ICS205A_TABLE_MIN_WIDTH_PX }}
      >
        <thead>
          <tr>
            <th className={`${HEADER_CELL} tw:w-10`}>
              {ICS205A_COLUMN_LABELS.index}
            </th>
            <th className={`${HEADER_CELL} tw:min-w-[9rem]`}>
              {ICS205A_COLUMN_LABELS.assignment}
            </th>
            <th className={`${HEADER_CELL} tw:min-w-[10rem]`}>
              {ICS205A_COLUMN_LABELS.name}
            </th>
            <th className={`${HEADER_CELL} tw:min-w-[8rem]`}>
              {ICS205A_COLUMN_LABELS.cellPhone}
            </th>
            <th className={`${HEADER_CELL} tw:min-w-[9rem]`}>
              {ICS205A_COLUMN_LABELS.radioFrequency}
            </th>
            <th className={`${HEADER_CELL} tw:min-w-[10rem]`}>
              {ICS205A_COLUMN_LABELS.other}
            </th>
            {canEdit ? <th className={`${HEADER_CELL} tw:w-12`} /> : null}
          </tr>
        </thead>
        <tbody>
          {contacts.length === 0 ? (
            <tr>
              <td
                colSpan={canEdit ? 7 : 6}
                className={`${CELL} tw:py-6 tw:text-center tw:text-neutral-400`}
              >
                No contacts yet. Add a contact or fill from roster.
              </td>
            </tr>
          ) : (
            contacts.map((contact, index) => {
              const key = contactKey(contact);
              return (
                <tr key={key}>
                  <td className={`${CELL} tw:text-neutral-400`}>{index + 1}</td>
                  <td className={CELL}>
                    {canEdit ? (
                      <PratusSelect
                        options={(() => {
                          if (
                            contact.assignment &&
                            !positionOptions.some(
                              (o) => o.value === contact.assignment
                            )
                          ) {
                            return [
                              {
                                value: contact.assignment,
                                label: contact.assignment,
                              },
                              ...positionOptions,
                            ];
                          }
                          return positionOptions;
                        })()}
                        value={
                          contact.assignment
                            ? {
                                value: contact.assignment,
                                label: contact.assignment,
                              }
                            : null
                        }
                        onChange={(option) => {
                          const selected = option as OptionType | null;
                          onPatchContact(contact, {
                            assignment: selected?.value
                              ? String(selected.value)
                              : '',
                          });
                        }}
                        placeholder="Assignment"
                        isClearable
                      />
                    ) : (
                      contact.assignment || '—'
                    )}
                  </td>
                  <td className={CELL}>
                    {canEdit ? (
                      <input
                        ref={(el) => {
                          nameInputRefs.current[key] = el;
                        }}
                        className={ICS205A_INPUT}
                        value={contact.name}
                        onChange={(e) =>
                          onPatchContact(contact, { name: e.target.value })
                        }
                        placeholder="Name"
                      />
                    ) : (
                      contact.name || '—'
                    )}
                  </td>
                  <td className={CELL}>
                    {canEdit ? (
                      <input
                        className={ICS205A_INPUT}
                        value={contact.cell_phone}
                        onChange={(e) =>
                          onPatchContact(contact, {
                            cell_phone: e.target.value,
                          })
                        }
                        placeholder="Cell phone"
                      />
                    ) : (
                      contact.cell_phone || '—'
                    )}
                  </td>
                  <td className={CELL}>
                    {canEdit ? (
                      <input
                        className={ICS205A_INPUT}
                        value={contact.radio_frequency}
                        onChange={(e) =>
                          onPatchContact(contact, {
                            radio_frequency: e.target.value,
                          })
                        }
                        placeholder="Radio frequency"
                      />
                    ) : (
                      contact.radio_frequency || '—'
                    )}
                  </td>
                  <td className={CELL}>
                    {canEdit ? (
                      <input
                        className={ICS205A_INPUT}
                        value={contact.other}
                        onChange={(e) =>
                          onPatchContact(contact, { other: e.target.value })
                        }
                        placeholder="Other"
                      />
                    ) : (
                      contact.other || '—'
                    )}
                  </td>
                  {canEdit ? (
                    <td className={`${CELL} tw:text-right`}>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className={`${ICS205A_ICON_BTN} ${ICS205A_TRASH_BTN} tw:h-8 tw:w-8`}
                        onClick={() => onDeleteContact(contact)}
                        aria-label="Delete contact"
                      >
                        <Trash2 className="tw:h-4 tw:w-4" />
                      </Button>
                    </td>
                  ) : null}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};
